/**
 * GL-DESIGN-V3 — Pre-delivery check modal (SPEC §13, five checks).
 *
 * 560px card, radius 30, on the surface color. Header: 56px navy
 * shield-check circle (gold icon) + "N passed, M to review". Checks render
 * as an inset list — 28px status circle (green check / P2 "!") + title +
 * detail. Footer: "Fix …" OutlinePill (jumps to the offending tab) and the
 * "Send anyway" PrimaryPill. A missing contact email blocks sending.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ShieldCheckIcon, CheckIcon, SpinnerGapIcon } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import { V3, PrimaryPill, OutlinePill, IconButton } from '@/components/v3/kit';

const FIRST_PERSON = /\b(I|I'm|I'd|I'll|I've|we|we're|we'd|we'll|going to|gonna|don't remember|not sure)\b/i;

/** Run the five pre-delivery checks. Returns [{ key, ok, blocking, title, detail, fixTab, fixLabel }]. */
export async function runPreDeliveryChecks(eng) {
  const [findingsRes, covRes, settingsRes] = await Promise.all([
    api.get(`/engagements/${eng.id}/findings`).catch(() => ({ data: [] })),
    api.get(`/engagements/${eng.id}/doc_coverage`).catch(() => ({ data: { buckets: [] } })),
    api.get('/settings').catch(() => ({ data: {} })),
  ]);
  const findings = findingsRes.data || [];
  const cov = covRes.data || { buckets: [] };
  const settings = settingsRes.data || {};

  const firstPerson = [];
  const psm = [];
  for (const f of findings) {
    for (const field of ['description', 'corrective_action']) {
      if (FIRST_PERSON.test(f[field] || '')) firstPerson.push({ number: f.number, where: field.replace('_', ' ') });
    }
    const cfr = (f.cfr_citation || '').toLowerCase();
    if (cfr.includes('1910.119') && !/chemical|psm|process safety|highly hazard|flammable/i.test(`${f.description || ''} ${f.violation_type || ''}`)) {
      psm.push(f.number);
    }
  }
  const unsynced = [];
  for (const b of cov.buckets || []) {
    for (const d of b.docs || []) {
      for (const field of ['notes', 'corrective_action']) {
        if (FIRST_PERSON.test(d[field] || '')) firstPerson.push({ number: null, where: `doc "${d.label}"` });
      }
      if (['missing', 'needs_revision', 'present_with_gaps'].includes(d.user_state) && !d.linked_finding) unsynced.push(d.label);
    }
  }
  const disc = (settings.disclaimer || '').trim();
  const badDisclaimer = !disc || /^(TEST_|placeholder|XXX|LOREM)/i.test(disc) || disc.length < 80;
  const firstFinding = firstPerson.find((x) => x.number)?.number ?? null;

  return [
    {
      key: 'contact',
      ok: !!eng.contact_email,
      blocking: true,
      title: 'Contact email on file',
      detail: eng.contact_email ? `Sending to ${eng.contact_email} · you are BCC'd` : 'No client email on the Setup tab — sending is blocked.',
      fixTab: 'setup',
      fixLabel: 'Add email',
    },
    {
      key: 'disclaimer',
      ok: !badDisclaimer,
      blocking: false,
      title: 'Disclaimer filled in',
      detail: badDisclaimer ? 'Settings → Disclaimer is blank or a placeholder. The PDF falls back to the default wording.' : 'Production disclaimer prints on the closing sheet.',
      fixTab: null,
      fixLabel: 'Open Settings',
      fixHref: '/settings',
    },
    {
      key: 'psm',
      ok: psm.length === 0,
      blocking: false,
      title: 'No PSM citations on non-chemical findings',
      detail: psm.length ? `Finding ${psm.map((n) => String(n).padStart(2, '0')).join(', ')} cites 1910.119 without a chemical-process context.` : 'Every 1910.119 citation sits on a chemical-handling finding.',
      fixTab: 'findings',
      fixLabel: psm.length ? `Fix Finding ${String(psm[0]).padStart(2, '0')}` : '',
    },
    {
      key: 'sync',
      ok: unsynced.length === 0,
      blocking: false,
      title: 'Docs and findings match',
      detail: unsynced.length ? `${unsynced.length} program${unsynced.length === 1 ? '' : 's'} flagged without a finding (${unsynced.slice(0, 3).join(', ')}${unsynced.length > 3 ? '…' : ''}). Regenerate to sync.` : 'Every flagged program has a matching finding in the dossier.',
      fixTab: 'coverage',
      fixLabel: 'Open Docs',
    },
    {
      key: 'voice',
      ok: firstPerson.length === 0,
      blocking: false,
      title: 'Client-ready wording',
      detail: firstPerson.length ? `${firstPerson.length} first-person phrase${firstPerson.length === 1 ? '' : 's'} ("I", "we", "going to") would print on the client sheets.` : 'No first-person phrasing in findings or doc notes.',
      fixTab: 'findings',
      fixLabel: firstFinding ? `Fix Finding ${String(firstFinding).padStart(2, '0')}` : 'Open Findings',
    },
  ];
}

export default function PreDeliveryModal({ eng, onClose, onSend, onFix }) {
  const [checks, setChecks] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    runPreDeliveryChecks(eng).then((c) => alive && setChecks(c));
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      alive = false;
      window.removeEventListener('keydown', onKey);
    };
  }, [eng, onClose]);

  const passed = (checks || []).filter((c) => c.ok).length;
  const review = (checks || []).filter((c) => !c.ok);
  const blocked = review.some((c) => c.blocking);
  const firstFix = review.find((c) => c.fixLabel);

  async function send() {
    setSending(true);
    try {
      await onSend();
    } finally {
      setSending(false);
    }
  }

  const node = (
    <div
      data-testid="pre-delivery-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(10,21,53,.45)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div role="dialog" aria-label="Pre-delivery check" style={{ width: 560, maxWidth: '100%', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', background: V3.surface, borderRadius: 30, boxShadow: V3.raisedLg, padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ width: 56, height: 56, borderRadius: '50%', background: V3.navy, color: V3.gold, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ShieldCheckIcon size={28} weight="fill" />
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: V3.mono, fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: V3.muted, fontWeight: 700 }}>Pre-delivery check</div>
            <div data-testid="pre-delivery-headline" style={{ fontSize: 22, fontWeight: 700, color: V3.navy, letterSpacing: '-0.02em' }}>
              {!checks ? 'Checking the dossier…' : review.length === 0 ? `All ${passed} passed` : `${passed} passed, ${review.length} to review`}
            </div>
          </div>
          <IconButton icon="x" size={44} onClick={onClose} testid="pre-delivery-close" title="Close" />
        </div>

        <div data-testid="pre-delivery-checks" style={{ background: V3.surface, borderRadius: 22, boxShadow: V3.insetSm, padding: '6px 16px' }}>
          {!checks && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 0', color: V3.muted, fontSize: 14 }}>
              <SpinnerGapIcon size={18} className="animate-spin" /> Scanning findings, docs and settings
            </div>
          )}
          {(checks || []).map((c, i) => (
            <div key={c.key} data-testid={`pre-delivery-check-${c.key}`} data-ok={c.ok} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '14px 0', borderTop: i ? `1px solid rgba(20,38,84,.08)` : 0 }}>
              <span style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: c.ok ? V3.p3 : V3.p2, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15 }}>
                {c.ok ? <CheckIcon size={15} weight="bold" /> : '!'}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: V3.ink }}>{c.title}</div>
                <div style={{ fontSize: 13, color: V3.muted, marginTop: 2, lineHeight: 1.45 }}>{c.detail}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          {firstFix ? (
            <OutlinePill
              testid="pre-delivery-fix"
              onClick={() => {
                onClose();
                if (firstFix.fixHref) window.location.assign(firstFix.fixHref);
                else onFix?.(firstFix.fixTab);
              }}
            >
              {firstFix.fixLabel}
            </OutlinePill>
          ) : <span />}
          <span aria-disabled={blocked || !checks || sending} data-testid="pre-delivery-send-wrap" style={{ pointerEvents: blocked || !checks || sending ? 'none' : 'auto' }}>
            <PrimaryPill
              icon="paper-plane-tilt"
              height={52}
              testid="pre-delivery-send"
              onClick={() => !blocked && !sending && checks && send()}
              style={{ opacity: blocked || !checks || sending ? 0.5 : 1, cursor: blocked ? 'not-allowed' : 'pointer' }}
            >
              {sending ? 'Sending…' : blocked ? 'Add an email to send' : review.length ? 'Send anyway' : 'Send to client'}
            </PrimaryPill>
          </span>
        </div>
      </div>
    </div>
  );
  return createPortal(node, document.body);
}
